import { RightOutlined } from '@ant-design/icons';
import useCountdown from '@bradgarropy/use-countdown';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useDebounceFn, useGetState, useInterval } from 'ahooks';
import { Alert, Button, Checkbox, Divider, message, Modal, QRCode, Radio, Space, Spin } from 'antd';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { get, post } from '../../../../../axios/axios';
import { useDevice } from '../../../../../hooks/useDevice';
import { DocType } from '../../../const';
import { summaryStore } from '../../../store';
import PackageModal from '../PackageModal';
import RedeemModal from '../RedeemModal';
import SelectCoupon from '../SelectCoupon/SelectCoupon';
import SelectUnlockChapter from '../SelectUnlockChapter/SelectUnlockChapter';
import { OrderModal } from '../../../../../components/Order/OrderModal';
import GroupBuyingOrder from '../../../../../components/GroupBuyingOrder/GroupBuyingOrder';
import {
    clearPackageEntitlement,
    getPackageDocType,
    isAmountEntitlement,
    selectPackageEntitlement,
    useSelectedPackageEntitlement,
} from '../../../../../Service/packageEntitlements';
import ali from './assets/ali.png';
import bg from './assets/bg.png';
import rectangle from './assets/rectangle.png';
import robotBg from './assets/robot_bg.svg';
import wx from './assets/wx.png';

// 解锁全章弹窗
const UnlockAllChapterModal = NiceModal.create(({ orderNo }) => {
    const modal = useModal();
    const [payResult, setPayResult] = useState(false);
    const [payInfo, setPayInfo, getPayInfo] = useGetState({});
    const [showPayResult, setShowPayResult] = useState(false);
    const [qrCodeStatus, setQrCodeStatus] = useState('loading');
    const [checking, setChecking] = useState(false);
    const [payType, setPayType] = useState(1); // 1:微信 2:支付宝
    const [alipayUrl, setAlipayUrl] = useState('');
    const [selectedPackage, setSelectedPackage] = useState('full'); // full: 全篇, single: 单章
    const [couponInfo, setCouponInfo] = useState(null); // 修改为存储完整的优惠券信息
    const [couponApplying, setCouponApplying] = useState(false);
    const selectedEntitlement = useSelectedPackageEntitlement();
    const selectedCoupon = Number(selectedEntitlement?.codeType) === 1 ? selectedEntitlement : null;
    const { isMobile } = useDevice();

    // 使用 useRef 管理状态
    const isPollingRef = useRef(false);
    const isDestroyedRef = useRef(false); // 替换 globalData.destroy
    const pollingIntervalRef = useRef(null); // 轮询间隔时间，null 表示停止轮询

    const useRedeemModal = useModal(RedeemModal);
    const useSelectUnlockChapter = useModal(SelectUnlockChapter);
    const usePackageModal = useModal(PackageModal);
    const useSelectCoupon = useModal(SelectCoupon);

    const counts = summaryStore.getSelectTypeCount();
    // 判断是否有额外的内容服务
    const hasExtraContent = (counts) => {
        return (
            counts &&
            (counts.TABLE > 0 ||
                counts.DIAGRAM > 0 ||
                counts.FORMULA > 0 ||
                counts.CODE > 0 ||
                counts.DATA > 0)
        );
    };

    // 倒计时
    const countdown = useCountdown({
        minutes: 30,
        seconds: 0,
        format: 'mm:ss',
        autoStart: false,
        onCompleted: () => console.log('倒计时结束'),
    });

    // 检查支付结果 - 由 useInterval 调用
    const checkPayStatus = async () => {
        // 如果已停止轮询，直接返回
        if (!isPollingRef.current) {
            console.log('[轮询] 已停止轮询标志');
            return;
        }

        console.log('[轮询] 检查支付状态', {
            orderNo: summaryStore.paperInfo.orderNo,
            isPolling: isPollingRef.current,
        });

        try {
            if (!summaryStore.paperInfo.orderNo) {
                console.error('[轮询] 订单号不存在');
                stopPolling();
                return;
            }

            const res = await get('/member/payment/pay/result/' + summaryStore.paperInfo.orderNo);
            console.log('[轮询] 支付状态检查结果:', res?.data);

            if (res?.data?.code === 0) {
                if (couponInfo) clearPackageEntitlement();
                console.log('[轮询] 支付成功，准备resolve');
                stopPolling();
                setShowPayResult(true);
                setPayResult(true);
                setTimeout(() => {
                    console.log('[轮询] 执行modal.resolve');
                    modal.resolve(res.data);
                    onCancel();
                }, 1000);
            } else {
                // 支付未完成，继续轮询（由 useInterval 自动处理）
                console.log('[轮询] 支付未完成，等待下次轮询');
            }
        } catch (err) {
            console.error('[轮询] 支付检查失败:', err);
            // 不要立即停止轮询，继续尝试（由 useInterval 自动处理）
            console.log('[轮询] 请求失败，等待下次重试');
        }
    };

    // 使用 ahooks 的 useInterval 替代 setTimeout
    useInterval(
        () => {
            checkPayStatus();
        },
        pollingIntervalRef.current, // null 时停止，有值时运行
    );

    // 停止轮询
    const stopPolling = () => {
        console.log('[轮询] 停止轮询');
        isPollingRef.current = false;
        pollingIntervalRef.current = null; // 设置为 null 会让 useInterval 停止
    };

    // 开始轮询
    const startPolling = (payInfoData = null) => {
        // 如果传入了 payInfoData，使用它；否则从 state 读取
        const payInfoToUse = payInfoData || getPayInfo();

        console.log('[轮询] 尝试开始轮询', {
            hasCodeUrl: !!payInfoToUse.codeUrl,
            hasForm: !!payInfoToUse.form,
            isMobile,
            destroyed: isDestroyedRef.current,
            orderNo: summaryStore.paperInfo.orderNo,
        });

        // 移动端支付宝支付也需要轮询
        // PC端微信需要有 codeUrl，PC端支付宝和移动端都需要有 form
        const shouldStartPolling = isDestroyedRef.current
            ? false
            : isMobile
              ? !!payInfoToUse.form // 移动端需要 form
              : payInfoToUse.codeUrl || payInfoToUse.form; // PC端微信需要codeUrl，支付宝需要form

        if (!shouldStartPolling) {
            console.log('[轮询] 条件不满足，不启动轮询');
            stopPolling();
            return;
        }

        console.log('[轮询] 启动轮询');
        isPollingRef.current = true;
        pollingIntervalRef.current = 1000; // 设置间隔时间会让 useInterval 开始
    };

    // 关闭弹窗
    const onCancel = () => {
        stopPolling();
        setPayInfo({});
        setShowPayResult(false);
        setPayType(1);
        setAlipayUrl('');
        modal.hide();
        isDestroyedRef.current = true; // 标记组件已销毁
    };

    // 重新支付
    const toRePay = async () => {
        try {
            setShowPayResult(false);
            setQrCodeStatus('loading');
            const res = await post('/member/payment/pay', {
                orderNo: summaryStore.paperInfo.orderNo,
                client: isMobile ? 'H5' : 'PC',
                payType: isMobile ? 2 : payType,
            });
            if (payType === 1 && res.data?.codeUrl && !isMobile) {
                setPayInfo(res.data);
                setQrCodeStatus('active');
                startPolling(res.data); // 传入最新的支付数据
            } else if ((payType === 2 || isMobile) && res.data?.form) {
                if (isMobile) {
                    window.location.href = res.data.form;
                } else {
                    setAlipayUrl(res.data.form);
                    setPayInfo(res.data);
                    startPolling(res.data); // PC端支付宝也需要轮询
                }
            }
        } catch (error) {
            console.error('支付请求失败:', error);
            setQrCodeStatus('expired');
        }
    };

    // 检查支付状态
    const checkPay = async () => {
        if (checking) return;

        try {
            setChecking(true);
            if (!summaryStore.paperInfo.orderNo) {
                console.error('[手动检查] 订单号不存在');
                message.error('支付信息不存在，请重新发起支付');
                return;
            }

            console.log('[手动检查] 停止自动轮询');
            stopPolling();

            const res = await get('/member/payment/pay/result/' + summaryStore.paperInfo.orderNo);
            console.log('[手动检查] 支付状态结果:', res?.data);

            if (res?.data?.code === 0) {
                if (couponInfo) clearPackageEntitlement();
                console.log('[手动检查] 支付成功');
                setShowPayResult(true);
                setPayResult(true);
                setTimeout(() => {
                    console.log('[手动检查] 执行modal.resolve');
                    modal.resolve(res.data);
                    onCancel();
                }, 1000);
            } else {
                console.log('[手动检查] 支付未完成，重新启动轮询');
                message.info('支付未完成，请扫码支付或选择其他支付方式');
                startPolling();
            }
        } catch (err) {
            console.error('[手动检查] 支付检查失败:', err);
            message.error('检查支付状态失败，请稍后再试');
            startPolling();
        } finally {
            setChecking(false);
        }
    };

    // 移动端立即支付
    const handleMobilePay = async () => {
        try {
            const res = await post('/member/payment/pay', {
                orderNo: summaryStore.paperInfo.orderNo,
                client: 'H5',
                payType: 2, // 支付宝
            });

            if (res?.data?.form) {
                window.location.href = res.data.form;
                // 开始轮询检查支付状态
                startPolling();
            } else {
                message.error('获取支付链接失败，请重试');
            }
        } catch (error) {
            console.error('移动端支付失败:', error);
            message.error('发起支付失败，请重试');
        }
    };

    // 根据文献类型显示解锁类型
    const showUnlockType = () => {
        if (summaryStore.docType === DocType.GRADUATION_THESIS) {
            return '解锁毕业长文';
        } else if (summaryStore.docType === DocType.TERM_PAPER) {
            return '解锁期刊长文';
        } else if (summaryStore.docType === DocType.PROPOSAL_REPORT) {
            return '解锁开题报告';
        } else if (summaryStore.docType === DocType.LITERATURE_REVIEW) {
            return '解锁文献综述';
        }
    };

    // 显示兑换码弹窗
    const redeemForCurrentOrder = async (initialCode = '') => {
        stopPolling();
        await useRedeemModal
            .show({
                orderNo: summaryStore.paperInfo.orderNo,
                initialCode,
            })
            .then((resData) => {
                console.log('unlockAllChapterModal完成--->resData', resData);

                setShowPayResult(true);
                setPayResult(true);
                setTimeout(() => {
                    modal.resolve(resData);
                    onCancel();
                }, 1000);
            })
            .catch(() => {
                refreshPaymentQRCode().catch(() => {});
            });
    };
    const showCodeModal = (e) => {
        e?.stopPropagation();
        return redeemForCurrentOrder();
    };

    const applySelectedEntitlement = (record) => {
        if (!record?.redeemCode) return;
        if (Number(record.codeType) === 1) {
            selectPackageEntitlement(record);
            handleSelectCoupon(record.redeemCode);
            return;
        }
        if (
            !isAmountEntitlement(record) &&
            ['DEFENSE_PPT', 'THESIS_MODIFICATION'].includes(getPackageDocType(record))
        ) {
            clearPackageEntitlement();
            throw new Error('请选择适用于当前文章的套餐；PPT或降重权益请在对应功能使用');
        }
        selectPackageEntitlement(record);
        redeemForCurrentOrder(record.redeemCode);
    };
    const showMyPackages = () =>
        NiceModal.show(GroupBuyingOrder, { onSelect: applySelectedEntitlement }).catch(() => {});
    const showPackageShop = () => {
        stopPolling();
        usePackageModal
            .show()
            .then((record) => {
                try {
                    applySelectedEntitlement(record);
                } catch (error) {
                    message.info(error.message);
                    startPolling();
                }
            })
            .catch(() => startPolling());
    };

    // 切换支付方式
    const handlePayTypeChange = async (e) => {
        const newPayType = e.target.value;
        setPayType(newPayType);
        setQrCodeStatus('loading');
        try {
            const res = await post('/member/payment/pay', {
                orderNo: summaryStore.paperInfo.orderNo,
                client: 'PC',
                payType: newPayType,
            });
            if (res.data?.codeUrl && newPayType === 1) {
                setPayInfo(res.data);
                setQrCodeStatus('active');
                startPolling(res.data); // 传入最新的支付数据
            } else if (res.data?.form && newPayType === 2) {
                setAlipayUrl(res.data.form);
                setPayInfo(res.data);
                startPolling(res.data); // 传入最新的支付数据
            }
        } catch (error) {
            console.error('支付请求失败:', error);
            setQrCodeStatus('expired');
        }
    };

    // 切换套餐类型
    const handlePackageChange = (e) => {
        setSelectedPackage(e.target.value);
    };

    const [searchChapterModalVisible, setSearchChapterModalVisible] = useState(false);
    const [searchChapterValue, setSearchChapterValue] = useState(null);
    // 处理单章点击，仅保留付费单章解锁
    const handleSingleChapterClick = async () => {
        const selectedChapter = await useSelectUnlockChapter.show({
            orderNo: summaryStore.paperInfo.orderNo,
        });

        console.log('handleSingleChapterClick--->selectedChapter', selectedChapter);
        if (selectedChapter?.orderNo) {
            setSearchChapterValue(selectedChapter.orderNo);
            setSearchChapterModalVisible(true);
        }
    };

    // 处理优惠券选择
    const handleSelectCoupon = async (initialCode = '') => {
        let quoteStarted = false;
        try {
            const couponData = await useSelectCoupon.show({
                initialCode: typeof initialCode === 'string' ? initialCode : '',
            });

            // 判断返回的数据类型（优惠券 or 专属口令）
            if (couponData?.type === 'exclusiveCode') {
                // 专属口令：提示成功，不更新价格（专属口令不影响价格）
                message.success('专属口令绑定成功');
            } else {
                // 优惠券：更新价格和订单号
                setCouponApplying(true);
                quoteStarted = true;
                stopPolling();
                setPayInfo({});
                setAlipayUrl('');
                const quote = await summaryStore.getPriceAfterAddServices({
                    discountCouponCode: couponData.code,
                });
                if (!quote?.orderNo) throw new Error('优惠券抵扣未完成，请重新确认');
                setCouponInfo(couponData);
                // 然后更新付款二维码信息
                await refreshPaymentQRCode();
                message.success('优惠券使用成功');
            }
        } catch (error) {
            if (error?.message && error.message !== '用户取消选择') message.error(error.message);
            if (quoteStarted) await refreshPaymentQRCode().catch(() => {});
        } finally {
            setCouponApplying(false);
            startPolling();
        }
    };

    // 添加二维码刷新函数
    const refreshPaymentQRCode = async () => {
        try {
            console.log('[刷新二维码] 开始刷新支付二维码');
            // 清理之前的轮询
            stopPolling();
            setQrCodeStatus('loading');

            const res = await post('/member/payment/pay', {
                orderNo: summaryStore.paperInfo.orderNo,
                client: isMobile ? 'H5' : 'PC',
                payType: isMobile ? 2 : payType,
            });

            console.log('[刷新二维码] 获取到新的支付信息', {
                hasCodeUrl: !!res.data?.codeUrl,
                hasForm: !!res.data?.form,
            });

            if (payType === 1 && res.data?.codeUrl && !isMobile) {
                setPayInfo(res.data);
                setQrCodeStatus('active');
                console.log('[刷新二维码] 微信支付，启动轮询');
                startPolling(res.data); // 传入最新的支付数据
            } else if ((payType === 2 || isMobile) && res.data?.form) {
                if (isMobile) {
                    // 移动端不直接跳转，等用户点击立即支付
                    setAlipayUrl(res.data.form);
                    setPayInfo(res.data);
                    console.log('[刷新二维码] 移动端支付宝，启动轮询');
                    // 移动端也需要重新开启轮询
                    startPolling(res.data); // 传入最新的支付数据
                } else {
                    setAlipayUrl(res.data.form);
                    setPayInfo(res.data);
                    console.log('[刷新二维码] PC端支付宝，启动轮询');
                    startPolling(res.data); // 传入最新的支付数据
                }
            }
            return res;
        } catch (error) {
            console.error('[刷新二维码] 获取支付二维码失败:', error);
            setQrCodeStatus('expired');
            throw error;
        }
    };

    // 防抖处理附加服务变更后的请求
    const { run: debouncedServiceChange } = useDebounceFn(
        async () => {
            try {
                const quote = await summaryStore.getPriceAfterAddServices({
                    discountCouponCode: couponInfo?.code,
                });
                if (!quote?.orderNo) throw new Error('更新服务价格失败，请重试');
                await refreshPaymentQRCode();
            } catch (error) {
                console.error('服务选择或支付信息更新失败:', error);
                message.error('更新支付信息失败，请重试');
            }
        },
        { wait: 500 },
    );

    useEffect(() => {
        let mounted = true;

        const initPayment = async () => {
            if (modal.visible && mounted) {
                try {
                    // 移动端默认使用支付宝，桌面端默认使用微信
                    setPayType(isMobile ? 2 : 1);
                    isDestroyedRef.current = false; // 重置销毁标志
                    setQrCodeStatus('loading');
                    const res = await post('/member/payment/pay', {
                        orderNo: summaryStore.paperInfo.orderNo,
                        client: isMobile ? 'H5' : 'PC',
                        payType: isMobile ? 2 : 1,
                    });

                    if (!mounted) return;

                    if (res.data.codeUrl && !isMobile) {
                        setPayInfo(res.data);
                        setQrCodeStatus('active');
                        countdown.start();
                        startPolling(res.data); // 传入最新的支付数据
                    } else if (res.data.form && isMobile) {
                        // 移动端保存支付链接但不自动跳转
                        setAlipayUrl(res.data.form);
                        setPayInfo(res.data);
                        // 移动端也需要开启轮询，以便在用户支付后自动检测
                        startPolling(res.data); // 传入最新的支付数据
                    } else if (res.data.form) {
                        // PC端支付宝
                        setAlipayUrl(res.data.form);
                        setPayInfo(res.data);
                        startPolling(res.data); // 传入最新的支付数据
                    }
                } catch (error) {
                    console.error('初始化支付失败:', error);
                    if (mounted) {
                        setQrCodeStatus('expired');
                    }
                }
            }
        };

        initPayment();

        return () => {
            mounted = false;
            countdown.reset();
            stopPolling();
            setPayType(1);
            setAlipayUrl('');
        };
    }, [modal.visible, isMobile]);

    // 过滤当前docType的附加服务列表
    const filterServiceList = () => {
        // 如果是开题报告，过滤掉开题报告
        if (summaryStore.docType === DocType.PROPOSAL_REPORT) {
            return summaryStore.serviceList.filter((service) => service?.sname !== '开题报告');
        }
        // 如果是文献综述，过滤掉文献综述
        if (summaryStore.docType === DocType.LITERATURE_REVIEW) {
            return summaryStore.serviceList.filter((service) => service?.sname !== '文献综述');
        }
        return summaryStore.serviceList;
    };
    return (
        <>
            <Modal
                title={null}
                open={modal.visible}
                width={isMobile ? '100%' : 1000}
                footer={null}
                onCancel={onCancel}
                maskClosable={false}
                styles={{
                    body: { padding: 0 },
                    content: { padding: 0 },
                }}
                zIndex={999}
            >
                <div
                    style={{
                        backgroundImage: `url(${bg})`,
                        backgroundSize: '100% 100%',
                    }}
                >
                    <div className="py-6 text-center text-xl font-bold text-[#5D1DC8]">
                        选择套餐
                    </div>
                    <div className="flex flex-col px-4 pb-[80px] pt-1 md:px-[56px]">
                        {showPayResult ? (
                            <div className="py-8 text-center">
                                <div>
                                    <p className="mb-4 text-lg font-medium">
                                        {payResult ? '恭喜您，支付成功' : '很遗憾，支付失败'}
                                    </p>
                                    {!payResult ? (
                                        <Button type="primary" onClick={toRePay}>
                                            重新支付
                                        </Button>
                                    ) : (
                                        <Button onClick={onCancel}>完成</Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* 套餐选择 */}
                                <div className="flex flex-col gap-4">
                                    <div className="flex-1 cursor-default rounded-xl border-2 border-primary bg-white p-4 shadow-lg">
                                        <div className="flex justify-between">
                                            <div className="flex-1">
                                                <div className="mb-2 text-lg font-medium text-[#192038]">
                                                    {showUnlockType()}
                                                </div>
                                                <div className="mb-2 text-sm text-[#8C93B1]">
                                                    {showUnlockType()}
                                                    可获得无限AI改写、一键降重、正文和插入图表
                                                </div>
                                            </div>
                                            <div className="ml-4 flex flex-col items-end">
                                                <span className="mb-2 text-2xl font-medium text-[#FB5D63]">
                                                    ¥
                                                    {
                                                        summaryStore.paperInfo?.prices
                                                            ?.totalDiscountedPrice
                                                    }
                                                </span>
                                                <span className="text-sm text-[#8C93B1] line-through">
                                                    ¥
                                                    {
                                                        summaryStore.paperInfo?.prices
                                                            ?.totalOriginalPrice
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className="flex-1 cursor-pointer rounded-xl border-2 border-gray-200 bg-white/50 p-4 transition-all hover:border-gray-300"
                                        onClick={handleSingleChapterClick}
                                    >
                                        <div className="flex justify-between">
                                            <div className="flex-1">
                                                <div className="mb-2 text-lg font-medium text-[#192038]">
                                                    解锁任意单章正文
                                                </div>
                                                <div className="mb-2 text-sm text-[#8C93B1]">
                                                    付费解锁任意单章内容，也可随时继续解锁全文内容
                                                </div>
                                            </div>
                                            <div className="ml-4 flex flex-col items-end">
                                                <span className="mb-2 text-2xl font-medium text-[#FB5D63]">
                                                    9.9付费解锁单章
                                                </span>
                                                <span className="text-sm text-[#8C93B1] line-through">
                                                    ¥29.99
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 附加服务 */}
                                <div className="mb-6">
                                    <Divider
                                        style={{
                                            margin: '32px 0',
                                        }}
                                    >
                                        <span className="text-base text-[#8C93B1]">附加服务</span>
                                    </Divider>
                                    <Spin spinning={summaryStore.isExtraloading}>
                                        {/* 新附加服务区域 */}
                                        {filterServiceList().filter(
                                            (service) => service?.stype?.split('_')?.[1] === 'aigc',
                                        ).length > 0 && (
                                            <div className="mb-4 rounded-lg bg-white p-3 shadow-[0px_4px_20px_0px_rgba(149,87,255,0.1)]">
                                                <div className="flex flex-wrap gap-4">
                                                    {filterServiceList()
                                                        .filter(
                                                            (service) =>
                                                                service?.stype?.split('_')?.[1] ===
                                                                'aigc',
                                                        )
                                                        .map((service) => (
                                                            <div
                                                                key={service.id}
                                                                className={`relative w-[calc(50%-0.5rem)] overflow-hidden rounded-xl p-4 transition-all md:w-[calc(25%-0.75rem)] ${
                                                                    service.checked
                                                                        ? 'border-2 border-primary bg-white'
                                                                        : 'border border-gray-200 bg-white/50'
                                                                }`}
                                                            >
                                                                <div className="flex items-start justify-between">
                                                                    <div>
                                                                        <div className="flex items-center text-base font-bold">
                                                                            {service.sname}
                                                                            <span className="ml-1 text-red-500">
                                                                                🔥
                                                                            </span>
                                                                        </div>
                                                                        {/* <div className="flex items-baseline">
                                                                        <span className="text-lg font-bold text-[#FB5D63]">
                                                                            ¥{service.price}
                                                                        </span>
                                                                        <span className="ml-2 text-xs text-gray-400 line-through">
                                                                            ¥{service.price2}
                                                                        </span>
                                                                    </div> */}
                                                                    </div>
                                                                    <div className="flex items-center self-stretch">
                                                                        <Checkbox
                                                                            checked={
                                                                                service.checked
                                                                            }
                                                                            disabled={
                                                                                summaryStore.isExtraloading
                                                                            }
                                                                            onChange={(e) => {
                                                                                summaryStore.onServiceCheck(
                                                                                    e.target
                                                                                        .checked,
                                                                                    service.stype,
                                                                                );
                                                                                debouncedServiceChange();
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {/* 机器人图标 */}
                                                                <div className="absolute -bottom-2 -right-2">
                                                                    <img
                                                                        src={robotBg}
                                                                        alt="AI机器人"
                                                                        className="h-[70px] w-[70px]"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* 原附加服务 */}
                                        <div className="flex flex-wrap gap-4">
                                            {filterServiceList()
                                                .filter(
                                                    (service) =>
                                                        service?.stype?.split('_')?.[1] !== 'aigc',
                                                )
                                                .map((service, index) => (
                                                    <div
                                                        key={service.id}
                                                        className={`w-[calc(50%-1rem)] rounded-xl p-4 transition-all md:w-[calc(33.33%-1rem)] ${
                                                            service.checked
                                                                ? 'border-2 border-primary bg-white shadow-lg'
                                                                : 'border border-gray-200 bg-white/50'
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div>
                                                                <div className="flex items-center text-base font-bold">
                                                                    {service.sname}
                                                                    {index % 2 === 0 && (
                                                                        <span className="ml-1 text-red-500">
                                                                            🔥
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {/* <div className="flex items-baseline">
                                                                <span className="text-lg font-bold text-[#FB5D63]">
                                                                    ¥{service.price}
                                                                </span>
                                                                <span className="ml-2 text-xs text-gray-400 line-through">
                                                                    ¥{service.price2}
                                                                </span>
                                                            </div> */}
                                                            </div>
                                                            <div className="flex items-center self-stretch">
                                                                <Checkbox
                                                                    checked={service.checked}
                                                                    disabled={
                                                                        summaryStore.isExtraloading
                                                                    }
                                                                    onChange={(e) => {
                                                                        summaryStore.onServiceCheck(
                                                                            e.target.checked,
                                                                            service.stype,
                                                                        );
                                                                        debouncedServiceChange();
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </Spin>
                                </div>

                                {/* 优惠券选择区域 */}
                                {selectedCoupon &&
                                    couponInfo?.code !== selectedCoupon.redeemCode && (
                                        <Alert
                                            className="mb-4"
                                            type="info"
                                            showIcon
                                            message={`已选择：${selectedCoupon.redeemName || '套餐优惠券'}`}
                                            description="确认抵扣规则后，将应用到当前文章订单。"
                                            action={
                                                <Button
                                                    size="small"
                                                    loading={couponApplying}
                                                    onClick={() =>
                                                        handleSelectCoupon(
                                                            selectedCoupon.redeemCode,
                                                        )
                                                    }
                                                >
                                                    确认使用优惠券
                                                </Button>
                                            }
                                        />
                                    )}
                                <div
                                    className="mb-6 flex cursor-pointer items-center justify-between rounded-lg border border-[#EEEEF1] bg-white p-4 transition-all hover:border-primary"
                                    onClick={handleSelectCoupon}
                                >
                                    <div className="flex items-center">
                                        <span className="text-base font-medium text-[#192038]">
                                            优惠券/专属口令
                                        </span>
                                        {couponInfo && (
                                            <span className="ml-2 text-sm text-[#FB5D63]">
                                                已选择优惠券
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center">
                                        <span className="mr-2 text-sm text-[#FB5D63]">
                                            {couponInfo
                                                ? `满${couponInfo.price}减${couponInfo.discount}`
                                                : '选择优惠券'}
                                        </span>
                                        <RightOutlined className="text-[#8C93B1]" />
                                    </div>
                                </div>

                                <div className="mb-6 flex flex-wrap items-center justify-center gap-3 rounded-xl border border-[#dce9e0] bg-white p-4">
                                    <span className="text-sm text-[#70817a]">
                                        已有套餐可直接用于当前文章
                                    </span>
                                    <Button onClick={showMyPackages} disabled={couponApplying}>
                                        我的套餐
                                    </Button>
                                    <Button onClick={showCodeModal} disabled={couponApplying}>
                                        我有兑换码
                                    </Button>
                                    <Button type="link" onClick={showPackageShop}>
                                        购买套餐
                                    </Button>
                                </div>

                                {/* 底部支付区域 */}
                                <div className="mt-auto">
                                    <div className="flex flex-col gap-6 md:flex-row md:justify-center md:gap-6">
                                        {/* 左侧二维码 - 仅桌面端显示 */}
                                        {!isMobile && (
                                            <div>
                                                <div
                                                    className="relative mx-auto mb-4 h-[200px] w-[200px] rounded-lg border border-[#DBE0EC]"
                                                    style={{
                                                        backgroundImage: `url(${rectangle})`,
                                                        backgroundSize: '100% 100%',
                                                        backgroundRepeat: 'no-repeat',
                                                    }}
                                                >
                                                    {payType === 1 ? (
                                                        payInfo.codeUrl ? (
                                                            <QRCode
                                                                value={payInfo.codeUrl}
                                                                status={qrCodeStatus}
                                                                onLoad={() =>
                                                                    setQrCodeStatus('active')
                                                                }
                                                                onError={() =>
                                                                    setQrCodeStatus('expired')
                                                                }
                                                                size={192}
                                                                className="absolute left-[4px] top-[4px]"
                                                            />
                                                        ) : (
                                                            <div className="flex h-full items-center justify-center text-sm text-gray-400">
                                                                正在加载二维码...
                                                            </div>
                                                        )
                                                    ) : null}
                                                    {payType === 2 && alipayUrl && (
                                                        <div className="ml-5 mt-5">
                                                            <iframe
                                                                className="h-[170px] w-[170px]"
                                                                src={alipayUrl}
                                                                title="支付宝支付"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* 右侧内容区 */}
                                        <div className="flex w-full flex-col items-center md:w-80 md:flex-col">
                                            {/* 右上扫码文案 - 仅桌面端显示 */}
                                            {!isMobile && (
                                                <div className="mb-2 text-left text-sm font-medium text-[#8C93B1]">
                                                    {payType === 1 ? '微信扫码支付' : '支付宝支付'}
                                                </div>
                                            )}

                                            {/* 右中价格区域 */}
                                            <div
                                                className={
                                                    hasExtraContent(counts) ? 'mb-3' : 'mb-8'
                                                }
                                            >
                                                <div className="flex items-baseline justify-between">
                                                    <div>
                                                        <span className="text-2xl font-bold text-[#FB5D63]">
                                                            ¥
                                                            {
                                                                summaryStore.paperInfo?.prices
                                                                    ?.totalDiscountedPrice
                                                            }
                                                        </span>
                                                        <span className="ml-2 text-sm text-[#8C93B1] line-through">
                                                            ¥
                                                            {
                                                                summaryStore.paperInfo?.prices
                                                                    ?.totalOriginalPrice
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 统计的大纲额外服务数量 */}
                                            {hasExtraContent(counts) && (
                                                <div className="mb-3 text-sm text-[#8C93B1]">
                                                    {counts.TABLE > 0 && (
                                                        <span className="mr-2">
                                                            [数据(表)×{counts.TABLE}-¥
                                                            {counts.TABLE * 3}]
                                                        </span>
                                                    )}
                                                    {counts.DIAGRAM > 0 && (
                                                        <span className="mr-2">
                                                            [图×{counts.DIAGRAM}-¥
                                                            {counts.DIAGRAM * 5}]
                                                        </span>
                                                    )}
                                                    {counts.DATA > 0 && (
                                                        <span className="mr-2">
                                                            [数据×{counts.DATA}-¥{counts.DATA * 3}]
                                                        </span>
                                                    )}
                                                    {counts.FORMULA > 0 && (
                                                        <span className="mr-2">
                                                            [公式×{counts.FORMULA}-¥
                                                            {counts.FORMULA * 3}]
                                                        </span>
                                                    )}
                                                    {counts.CODE > 0 && (
                                                        <span className="mr-2">
                                                            [代码段×{counts.CODE}-¥{counts.CODE * 3}
                                                            ]
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* 桌面端支付方式切换 */}
                                            {!isMobile && (
                                                <div className="mb-4 flex w-full gap-2">
                                                    <div
                                                        className={`flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border px-3 py-2 ${
                                                            payType === 1
                                                                ? 'border-primary bg-white'
                                                                : 'border-gray-200 bg-white/50'
                                                        }`}
                                                        onClick={() =>
                                                            handlePayTypeChange({
                                                                target: { value: 1 },
                                                            })
                                                        }
                                                    >
                                                        <img
                                                            src={wx}
                                                            alt="微信"
                                                            className="h-5 w-5"
                                                        />
                                                        微信支付
                                                    </div>
                                                    <div
                                                        className={`flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border px-3 py-2 ${
                                                            payType === 2
                                                                ? 'border-primary bg-white'
                                                                : 'border-gray-200 bg-white/50'
                                                        }`}
                                                        onClick={() =>
                                                            handlePayTypeChange({
                                                                target: { value: 2 },
                                                            })
                                                        }
                                                    >
                                                        <img
                                                            src={ali}
                                                            alt="支付宝"
                                                            className="h-5 w-5"
                                                        />
                                                        支付宝支付
                                                    </div>
                                                </div>
                                            )}

                                            <Button
                                                type="primary"
                                                onClick={checkPay}
                                                loading={checking}
                                                disabled={
                                                    checking ||
                                                    couponApplying ||
                                                    summaryStore.isExtraloading
                                                }
                                                className="hover:!bg-primary/90 mb-3 !h-10 w-full !rounded-lg !bg-primary"
                                            >
                                                我已支付成功
                                            </Button>

                                            {/* 移动端立即支付按钮 */}
                                            {isMobile && (
                                                <Button
                                                    type="default"
                                                    onClick={handleMobilePay}
                                                    disabled={
                                                        couponApplying ||
                                                        summaryStore.isExtraloading
                                                    }
                                                    className="hover:!bg-primary/10 mb-3 !h-10 w-full !rounded-lg !border-primary !text-primary"
                                                >
                                                    立即支付
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Modal>
            {/* 嵌套的订单查询Modal */}
            <Modal
                title="订单查询"
                open={searchChapterModalVisible}
                onCancel={() => setSearchChapterModalVisible(false)}
                footer={null}
                width={isMobile ? '100%' : 800}
                zIndex={1001}
            >
                <OrderModal
                    initialOrderNo={searchChapterValue}
                    handleOk={() => setSearchChapterModalVisible(false)}
                    editBtnText="编辑"
                />
            </Modal>
        </>
    );
});

export default observer(UnlockAllChapterModal);
